import { Theme } from '@mui/material';
import { green, grey, indigo } from '@mui/material/colors';

import { NO_DEPARTMENT } from '../constants/organizations';
import { URL_VARS } from '../constants/routing';
import {
  DEFAULT_BUTTON_COLOR,
  EMAIL_TEMPLATE_TYPES,
  EMPTY_RICH_TEXT_HTML,
  HEADER_MEDIA_EMPTY,
  HEADER_TEXT_REQUIRED_ERROR,
  INITIAL_TEMPLATE_EMAIL_FORM_STATE,
  INITIAL_TEMPLATE_NOTE_FORM_STATE,
  LOGO_SIZE_DEFAULT,
  LOGO_SIZE_MAX,
  LOGO_SIZE_MIN,
  TEMPLATE_TOKENS_REGEX,
  TOKENS_MAP,
  VALIDATED_FIELDS_CONFIG,
} from '../constants/templates';
import { DEPT_ADMIN } from '../constants/users';
import { IDepartment } from '../types/organizations';
import { ITemplateRouteParams } from '../types/routing';
import { ImageFitEnum } from '../types/shell';
import {
  ClaimLinkEditorPageEnum,
  ClaimLinkTemplateStatusEnum,
  EmailTemplateTypesEnum,
  IClaimLinkBuilderPageValues,
  IClaimLinkBuilderValues,
  IClaimLinkPageOverrides,
  IClaimLinkTemplate,
  IClaimLinkTemplateErrors,
  IClaimLinkTemplatePageContent,
  IClaimLinkTemplatePageOverrides,
  IEmailTemplate,
  IExperiencePreviewPageOverrides,
  IframeStepEnum,
  INoteTemplate,
  IPageErrors,
  IParsedTemplateErrors,
  ITimeUnit,
  IValidatedFieldConfig,
  TemplateTypesEnum,
} from '../types/templates';
import { BackgroundTypesEnum, CSSBackgroundPositionEnum, ImageSizeEnum } from '../types/upload';
import { hexToRgbColor, rgbaToRgbaString } from './helpers';
import { hasPermission } from './users';

type TPageFieldValue = string | number | object | undefined;

export const replaceLineBreaks = (template: string) => template.replace(/(\\n|\\\\n)/g, '\n');

const HTML_BLOCK_BOUNDARY = /<\/(?:p|div|li|ul|ol|h[1-6]|tr|blockquote)>|<br\s*\/?>/gi;

const HTML_ENTITIES: [RegExp, string][] = [
  [/&nbsp;/gi, ' '],
  [/&lt;/gi, '<'],
  [/&gt;/gi, '>'],
  [/&quot;/gi, '"'],
  [/&#0?39;|&apos;/gi, "'"],
  [/&amp;/gi, '&'],
];

export const stripHtmlToText = (html: string): string =>
  HTML_ENTITIES.reduce(
    (text, [pattern, replacement]) => text.replace(pattern, replacement),
    html.replace(HTML_BLOCK_BOUNDARY, ' ').replace(/<[^>]*>/g, ''),
  )
    .replace(/\s+/g, ' ')
    .trim();

export const isHtmlBlank = (html: string): boolean => stripHtmlToText(html).length === 0;

const EMPTY_INLINE_TAG = '(?:strong|em|b|i|u|s|span|sub|sup)';

const EMPTY_BLOCK_REGEX = new RegExp(
  `<(div|p)(\\s[^>]*)?>((?:\\s|<${EMPTY_INLINE_TAG}(?:\\s[^>]*)?>)*)((?:<\\/${EMPTY_INLINE_TAG}>\\s*)*)<\\/\\1>`,
  'gi',
);

// TinyMCE serializes the block a double Enter leaves as `<div></div>`, or `<div><strong></strong></div>`
// when the caret carried formatting, and neither has height outside the editor. The padding goes inside
// the innermost tag so a font size on it still sets the row height. Pad on the way out only: a value fed
// back to the controlled editor resets its caret.
export const padEmptyBlocks = (html: string): string =>
  isHtmlBlank(html)
    ? html
    : html.replace(
        EMPTY_BLOCK_REGEX,
        (_, tag, attrs = '', opening = '', closing = '') =>
          `<${tag}${attrs}>${opening.replace(/(^|>)\s+/g, '$1')}&nbsp;${closing.replace(/\s+/g, '')}</${tag}>`,
      );

const NESTED_BLOCK_SELECTOR = 'div,p,ul,ol,li,table,h1,h2,h3,h4,h5,h6,blockquote,pre,hr';

// Text saved before Enter started paragraphs keeps its lines in root `<div>`s, and TinyMCE clones the
// current block on Enter, so Enter inside that text would keep adding unspaced `<div>`s. Promote each
// root `<div>` holding only inline content to a `<p>` so old and new text get the same spacing.
export const divBlocksToParagraphs = (html: string): string => {
  if (isHtmlBlank(html) || !/<div[\s>]/i.test(html)) {
    return html;
  }

  const container = document.createElement('template');
  container.innerHTML = html;
  const legacyBlocks = Array.from(container.content.children).filter(
    (element) => element.tagName === 'DIV' && !element.querySelector(NESTED_BLOCK_SELECTOR),
  );

  if (!legacyBlocks.length) {
    return html;
  }

  legacyBlocks.forEach((block) => {
    const paragraph = document.createElement('p');
    Array.from(block.attributes).forEach(({ name, value }) => paragraph.setAttribute(name, value));
    paragraph.append(...Array.from(block.childNodes));
    block.replaceWith(paragraph);
  });

  return container.innerHTML;
};

/*
 * The app bar logo size is a percent (50-200) in the UI and a 0.5-2 float ratio on the wire.
 * Convert only at the transform boundary so a percent is never persisted.
 */
export const clampLogoSizePercent = (value: number) => Math.max(LOGO_SIZE_MIN, Math.min(LOGO_SIZE_MAX, value));

export const ratioToLogoSizePercent = (ratio?: number): number => {
  if (typeof ratio !== 'number' || !Number.isFinite(ratio) || ratio <= 0) {
    return LOGO_SIZE_DEFAULT;
  }

  return clampLogoSizePercent(Math.round(ratio * 100));
};

export const logoSizePercentToRatio = (percent?: number): number => {
  if (typeof percent !== 'number' || !Number.isFinite(percent)) {
    return LOGO_SIZE_DEFAULT / 100;
  }

  return clampLogoSizePercent(percent) / 100;
};

/**
 * Normalizes a template/experience id, treating blank strings as "not set".
 * Legacy records can persist an empty-string `experience_id` instead of omitting it; `??` only
 * catches null/undefined, so a raw "" would short-circuit priority cascades (e.g. campaign → org
 * default). Coalescing blanks to null keeps those fallbacks working.
 */
export const normalizeExperienceId = (value?: string | null): string | null => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
};

/**
 * Builds iframe preview page overrides from claim link builder page overrides.
 * Only defined values are included in the result, and color values are converted
 * to RGBA strings for preview compatibility.
 *
 * Image URLs (header media, hero image) are deliberately NOT sent as overrides: the claim
 * page treats any non-empty override URL as a real image (it has no "removed" sentinel) and
 * '' falls back to the template value, so extractPreviewValues resolves the active page's
 * effective image into the template-level slot instead.
 *
 * @param overrides - Claim link page override values from the builder form
 * @returns Experience preview overrides to send to the iframe
 */
const buildPageOverrides = (overrides?: IClaimLinkPageOverrides): IExperiencePreviewPageOverrides => {
  if (!overrides) {
    return {};
  }

  const result: IExperiencePreviewPageOverrides = {};

  if (
    overrides.logoImageUrl !== undefined ||
    overrides.logoImageSize !== undefined ||
    overrides.appBarBackgroundColor !== undefined
  ) {
    result.header = {
      ...result.header,
      app_bar: {
        ...(result.header?.app_bar ?? {}),
        ...(overrides.logoImageUrl !== undefined && { logo_image_url: overrides.logoImageUrl }),
        ...(overrides.logoImageSize !== undefined && {
          logo_image_size: logoSizePercentToRatio(overrides.logoImageSize),
        }),
        ...(overrides.appBarBackgroundColor !== undefined && {
          background_color: rgbaToRgbaString(overrides.appBarBackgroundColor),
        }),
      },
    };
  }

  if (overrides.heroSectionBackgroundColor !== undefined || overrides.heroSectionImageFit !== undefined) {
    result.header = {
      ...result.header,
      hero_section: {
        ...(overrides.heroSectionBackgroundColor !== undefined && {
          background_color: rgbaToRgbaString(overrides.heroSectionBackgroundColor),
        }),
        ...(overrides.heroSectionImageFit !== undefined && { image_fit: overrides.heroSectionImageFit }),
      },
    };
  }

  if (overrides.buttonColor !== undefined) {
    result.buttons = { action: { color: rgbaToRgbaString(overrides.buttonColor) } };
  }

  return result;
};

// The API has no active-background discriminator, so a missing/empty hero image URL means the
// Color selection is active and a present one means Image. Used everywhere the active hero
// background type has to be derived from persisted values.
export const deriveHeroBackgroundType = (imageUrl?: string | null): BackgroundTypesEnum =>
  imageUrl ? BackgroundTypesEnum.Image : BackgroundTypesEnum.Color;

// Prefer the explicit FE-only selection when present, otherwise fall back to deriving it from
// the image URL. This is the shape used by the builder form and preview code.
export const resolveHeroBackgroundType = (
  explicitType: BackgroundTypesEnum | undefined,
  imageUrl?: string | null,
): BackgroundTypesEnum => explicitType ?? deriveHeroBackgroundType(imageUrl);

export const extractPreviewValues = (draft: IClaimLinkBuilderValues) => {
  // A cleared header/body must render empty on the canvas — the claim page falls back to its
  // hardcoded defaults on an empty string, so send visually-empty HTML instead.
  const toPreviewText = (text?: string) => padEmptyBlocks(divBlocksToParagraphs(text ?? '')) || EMPTY_RICH_TEXT_HTML;

  // The canvas shows the active page; resolve its hero media (page override → template default)
  // into the global slot so removal and per-page overrides are reflected even though the
  // claim page reads media from the template-level value.
  const activeOverrides = draft?.pages?.[draft?.activePageId]?.overrides;
  const resolvedHeaderMediaUrl =
    activeOverrides?.headerMediaUrl !== undefined ? activeOverrides.headerMediaUrl : draft?.globalHeaderMediaUrl;

  // Only the active background selection is sent: with Color active the image is withheld
  // so the color renders, while the uploaded image URL stays stored in the draft.
  const globalHeroBackgroundType = resolveHeroBackgroundType(
    draft?.globalHeroSectionBackgroundType,
    draft?.globalHeroSectionImageUrl,
  );

  // The claim page renders an image whenever it resolves a non-empty hero image URL (override
  // first, then template level) and only falls back to the color when both are '' — it has no
  // "image removed" sentinel. So the active page's effective hero image (page toggle/override →
  // template default) is resolved into the template-level slot, where '' means "render the color".
  let resolvedHeroImageUrl =
    globalHeroBackgroundType === BackgroundTypesEnum.Image ? draft?.globalHeroSectionImageUrl : '';
  if (activeOverrides?.heroSectionBackgroundType === BackgroundTypesEnum.Color) {
    resolvedHeroImageUrl = '';
  } else if (activeOverrides?.heroSectionImageUrl !== undefined) {
    resolvedHeroImageUrl = activeOverrides.heroSectionImageUrl;
  } else if (activeOverrides?.heroSectionBackgroundType === BackgroundTypesEnum.Image) {
    resolvedHeroImageUrl = draft?.globalHeroSectionImageUrl;
  }

  return {
    experience: {
      header: {
        media: {
          image_url: resolvedHeaderMediaUrl,
        },
        app_bar: {
          background_color: rgbaToRgbaString(draft?.globalAppBarBackgroundColor),
          logo_image_url: draft?.globalLogoImageUrl,
          logo_image_size: logoSizePercentToRatio(draft?.globalLogoImageSize),
        },
        hero_section: {
          background_color: rgbaToRgbaString(draft?.globalHeroSectionBackgroundColor),
          image_url: resolvedHeroImageUrl,
          image_position: draft?.globalHeroSectionImagePosition,
          image_fit: draft?.globalHeroSectionImageFit,
        },
      },
      buttons: {
        action: {
          color: rgbaToRgbaString(draft?.globalAccentColor),
        },
      },
      background: {
        color: rgbaToRgbaString(draft?.backgroundColor),
      },
      pages: {
        pick_your_gift: {
          header_text: toPreviewText(draft?.pages?.pick_your_gift?.headerText),
          body_text: toPreviewText(draft?.pages?.pick_your_gift?.bodyText),
          overrides: buildPageOverrides(draft?.pages?.pick_your_gift?.overrides),
        },
        pick_your_option: {
          header_text: toPreviewText(draft?.pages?.pick_your_option?.headerText),
          body_text: toPreviewText(draft?.pages?.pick_your_option?.bodyText),
          overrides: buildPageOverrides(draft?.pages?.pick_your_option?.overrides),
        },
        shipping_page: {
          header_text: toPreviewText(draft?.pages?.shipping_page?.headerText),
          body_text: toPreviewText(draft?.pages?.shipping_page?.bodyText),
          overrides: buildPageOverrides(draft?.pages?.shipping_page?.overrides),
        },
      },
    },
  };
};

export const setTemplateTokens = (template: string, values: any) => {
  if (!values) {
    return template;
  }

  try {
    const matches = template.match(TEMPLATE_TOKENS_REGEX);

    if (matches && matches.length) {
      matches.forEach((key) => {
        const rawKey = key.slice(1, -1);
        const keyToChange = TOKENS_MAP[rawKey] || null;

        if (keyToChange) {
          const tokenValue = values[keyToChange];
          const replacement = Array.isArray(tokenValue) ? tokenValue.join(', ') : tokenValue ?? '';
          template = template.replace(key, replacement);
        }
      });
    }
  } catch (e) {
    console.error('An error has occurred during template parsing', e);
  }

  return template;
};

export const sortByDepId =
  (depId: string) => (a: IEmailTemplate | INoteTemplate, b: IEmailTemplate | INoteTemplate) => {
    if (includesOnlyThisDep(a.department_ids, depId) !== includesOnlyThisDep(b.department_ids, depId)) {
      return includesOnlyThisDep(a.department_ids, depId) ? -1 : 1;
    }

    if (
      !includesOnlyThisDep(a.department_ids, depId) &&
      !includesOnlyThisDep(b.department_ids, depId) &&
      a.department_ids.includes(NO_DEPARTMENT) !== b.department_ids.includes(NO_DEPARTMENT)
    ) {
      return a.department_ids.includes(NO_DEPARTMENT) ? -1 : 1;
    }

    return ((a as INoteTemplate).title || (a as IEmailTemplate).subject).localeCompare(
      (b as INoteTemplate).title || (b as IEmailTemplate).subject,
    );
  };

export const getEmailTemplateLabelByType = (type: EmailTemplateTypesEnum) => {
  return EMAIL_TEMPLATE_TYPES.find((template) => template.value === type)?.label || '';
};

export const isTemplateEmail = (type: TemplateTypesEnum | null | undefined): boolean => {
  if (!type) {
    return false;
  }

  return type === TemplateTypesEnum.Email;
};

export const filterClaimLinkTemplatesForUser = (
  templates: IClaimLinkTemplate[],
  userDepartmentId: string | undefined,
  adminType: string,
): IClaimLinkTemplate[] => {
  if (!hasPermission([DEPT_ADMIN], adminType)) {
    return templates;
  }

  const scopeDepartmentId = userDepartmentId || NO_DEPARTMENT;

  return templates.filter(({ department_ids }) => {
    if (!Array.isArray(department_ids)) {
      return false;
    }

    return department_ids.includes(NO_DEPARTMENT) || department_ids.includes(scopeDepartmentId);
  });
};

export const includesOnlyThisDep = (depIds: string[], id: string) => {
  return depIds.length === 1 && depIds.includes(id);
};

export const getAddTemplateInitialValue = (type: TemplateTypesEnum, adminType: string, userDepartmentId: string) => {
  const value = isTemplateEmail(type) ? INITIAL_TEMPLATE_EMAIL_FORM_STATE : INITIAL_TEMPLATE_NOTE_FORM_STATE;
  return hasPermission([DEPT_ADMIN], adminType) ? { ...value, department_ids: [userDepartmentId] } : value;
};

export const getTemplateType = (type: TemplateTypesEnum) => {
  switch (type) {
    case TemplateTypesEnum.Email:
      return URL_VARS.EMAIL;
    case TemplateTypesEnum.Note:
      return URL_VARS.CUSTOM_NOTE;
    case TemplateTypesEnum.ClaimLink:
      return URL_VARS.CLAIM_LINK;
    default:
      return '';
  }
};

export const getTemplateTypeFromUrl = (urlType: ITemplateRouteParams['type']): TemplateTypesEnum | undefined => {
  switch (urlType) {
    case URL_VARS.EMAIL:
      return TemplateTypesEnum.Email;
    case URL_VARS.CUSTOM_NOTE:
      return TemplateTypesEnum.Note;
    default:
      return undefined;
  }
};

export const getTimeAgo = (utcDateString: string): string => {
  const now = new Date();
  const updatedDate = new Date(utcDateString);
  const differenceInMs = now.getTime() - updatedDate.getTime();

  // Convert to minutes as base unit
  const differenceInMinutes = Math.floor(differenceInMs / (1000 * 60));

  // Return early for "just now"
  if (differenceInMinutes < 1) {
    return 'Edited just now';
  }

  // Define time units with their thresholds and labels
  const timeUnits: ITimeUnit[] = [
    { value: Math.floor(differenceInMinutes / 525600), singular: 'year', plural: 'years' }, // 365 * 24 * 60
    { value: Math.floor(differenceInMinutes / 43200), singular: 'month', plural: 'months' }, // 30 * 24 * 60
    { value: Math.floor(differenceInMinutes / 10080), singular: 'week', plural: 'weeks' }, // 7 * 24 * 60
    { value: Math.floor(differenceInMinutes / 1440), singular: 'day', plural: 'days' }, // 24 * 60
    { value: Math.floor(differenceInMinutes / 60), singular: 'hour', plural: 'hours' },
    { value: differenceInMinutes, singular: 'minute', plural: 'minutes' },
  ];

  // Find the first non-zero unit
  for (const unit of timeUnits) {
    if (unit.value >= 1) {
      const label = unit.value === 1 ? unit.singular : unit.plural;
      return `Edited ${unit.value} ${label} ago`;
    }
  }

  return 'Edited just now';
};

export const getClaimLinkTemplateStatusChipProps = (status: ClaimLinkTemplateStatusEnum, theme: Theme) => {
  let label = '';
  let backgroundColor = '';
  let color = '';
  let dataAction = '';

  switch (status) {
    case ClaimLinkTemplateStatusEnum.Live:
      label = 'Live';
      backgroundColor = green[50];
      color = green[800];
      dataAction = 'live_status_chip';
      break;
    case ClaimLinkTemplateStatusEnum.Hidden:
      label = 'Hidden';
      backgroundColor = indigo[50];
      color = indigo[500];
      dataAction = 'hidden_status_chip';
      break;
    case ClaimLinkTemplateStatusEnum.Draft:
      label = 'Draft';
      backgroundColor = grey[200];
      color = theme.palette.text.secondary;
      dataAction = 'draft_status_chip';
      break;
    default:
      return null;
  }

  return { label, backgroundColor, color, dataAction };
};

export const transformClaimLinkTemplateToBuilderValues = (
  template: IClaimLinkTemplate,
  defaultBlueprint?: Partial<IClaimLinkTemplate>,
): IClaimLinkBuilderValues => {
  const wrapInRootBlock = (text: string): string => {
    if (!text) {
      return '';
    }
    const trimmed = text.trim();
    if (/^<(div|p|h[1-6]|ul|ol|blockquote|pre|table)\b/i.test(trimmed)) {
      return trimmed;
    }
    return `<div>${trimmed}</div>`;
  };

  // A cleared field is persisted as the EMPTY_RICH_TEXT_HTML sentinel — decode it back to an
  // empty editor value so the field stays visibly empty instead of showing markup remnants.
  const decodeClearedText = (text?: string): string => {
    if (!text || text === EMPTY_RICH_TEXT_HTML) {
      return '';
    }
    return text;
  };

  const transformPage = (
    pageData: IClaimLinkTemplatePageContent | undefined,
    apiPageKey: keyof IClaimLinkTemplate['pages'],
  ): IClaimLinkBuilderPageValues => {
    const apiOverrides = template.pages?.[apiPageKey]?.overrides;

    return {
      headerText: wrapInRootBlock(decodeClearedText(pageData?.header_text)),
      bodyText: wrapInRootBlock(decodeClearedText(pageData?.body_text)),
      overrides: {
        headerMediaUrl:
          apiOverrides?.header?.media?.image_url === HEADER_MEDIA_EMPTY
            ? ''
            : apiOverrides?.header?.media?.image_url || undefined,
        logoImageUrl: apiOverrides?.header?.app_bar?.logo_image_url || undefined,
        // undefined (not a default) so an absent override keeps inheriting the template value
        logoImageSize: apiOverrides?.header?.app_bar?.logo_image_size
          ? ratioToLogoSizePercent(apiOverrides.header.app_bar.logo_image_size)
          : undefined,
        appBarBackgroundColor: apiOverrides?.header?.app_bar?.background_color
          ? hexToRgbColor(apiOverrides.header.app_bar.background_color, DEFAULT_BUTTON_COLOR)
          : undefined,
        heroSectionBackgroundColor: apiOverrides?.header?.hero_section?.background_color
          ? hexToRgbColor(apiOverrides.header.hero_section.background_color, DEFAULT_BUTTON_COLOR)
          : undefined,
        heroSectionImageUrl:
          apiOverrides?.header?.hero_section?.image_url === HEADER_MEDIA_EMPTY
            ? ''
            : apiOverrides?.header?.hero_section?.image_url || undefined,
        heroSectionImageFit: apiOverrides?.header?.hero_section?.image_fit,
        heroSectionBackgroundType: undefined,
        buttonColor: apiOverrides?.buttons?.action?.color
          ? hexToRgbColor(apiOverrides.buttons.action.color, DEFAULT_BUTTON_COLOR)
          : undefined,
      },
    };
  };

  return {
    // Metadata
    uid: template.uid,
    templateName: template.name || '',
    selectedDepartmentIds: template.department_ids?.length ? template.department_ids : [NO_DEPARTMENT],
    status: template.status || ClaimLinkTemplateStatusEnum.Draft,
    is_default: template.is_default,

    // Global defaults (use template value OR blueprint as fallback)
    globalLogoImageUrl:
      template.header?.app_bar?.logo_image_url ?? defaultBlueprint?.header?.app_bar?.logo_image_url ?? '',
    globalLogoImageHref:
      template.header?.app_bar?.logo_image_href || defaultBlueprint?.header?.app_bar?.logo_image_href || '',
    globalLogoImageSize: ratioToLogoSizePercent(
      template.header?.app_bar?.logo_image_size ?? defaultBlueprint?.header?.app_bar?.logo_image_size,
    ),
    globalAppBarBackgroundColor: hexToRgbColor(
      template.header?.app_bar?.background_color || defaultBlueprint?.header?.app_bar?.background_color,
      DEFAULT_BUTTON_COLOR,
    ),
    globalHeaderMediaUrl: template.header?.media?.image_url ?? defaultBlueprint?.header?.media?.image_url ?? '',
    globalHeroSectionBackgroundColor: hexToRgbColor(
      template.header?.hero_section?.background_color || defaultBlueprint?.header?.hero_section?.background_color,
      DEFAULT_BUTTON_COLOR,
    ),
    globalHeroSectionImageUrl:
      template.header?.hero_section?.image_url ?? defaultBlueprint?.header?.hero_section?.image_url ?? '',
    globalHeroSectionImagePosition:
      template.header?.hero_section?.image_position ||
      defaultBlueprint?.header?.hero_section?.image_position ||
      CSSBackgroundPositionEnum.Center,
    // image_fit doubles as the hero height mode (Cover = Auto, Contain = Adapt); legacy
    // templates without it open as Cover / Auto.
    globalHeroSectionImageFit:
      template.header?.hero_section?.image_fit ||
      defaultBlueprint?.header?.hero_section?.image_fit ||
      ImageFitEnum.Cover,
    // The API has no active-background discriminator — derive it from image presence
    // (with Color active the image URL is saved empty, so this round-trips correctly).
    globalHeroSectionBackgroundType: deriveHeroBackgroundType(
      template.header?.hero_section?.image_url ?? defaultBlueprint?.header?.hero_section?.image_url,
    ),
    globalAccentColor: hexToRgbColor(
      template.buttons?.action?.color || defaultBlueprint?.buttons?.action?.color,
      DEFAULT_BUTTON_COLOR,
    ),
    globalButtonRadius: template.buttons?.action?.radius ?? defaultBlueprint?.buttons?.action?.radius ?? 8,
    backgroundColor: hexToRgbColor(
      template.background?.color || defaultBlueprint?.background?.color,
      DEFAULT_BUTTON_COLOR,
    ),
    coverImageUrl: template.cover_image_url || '',

    // Transform all 3 pages
    activePageId: ClaimLinkEditorPageEnum.PickYourGift,
    pages: {
      [ClaimLinkEditorPageEnum.PickYourGift]: transformPage(template.pages?.pick_your_gift, 'pick_your_gift'),
      [ClaimLinkEditorPageEnum.ProductOptions]: transformPage(template.pages?.pick_your_option, 'pick_your_option'),
      [ClaimLinkEditorPageEnum.ShippingDetails]: transformPage(template.pages?.shipping_page, 'shipping_page'),
    },

    // DEPRECATED - keeping for backward compatibility
    headerText: template.pages?.pick_your_gift?.header_text || '',
    bodyText: template.pages?.pick_your_gift?.body_text || '',
    logoImageUrl: template.header?.app_bar?.logo_image_url ?? null,
    headerMediaUrl: template.header?.media?.image_url ?? null,
    backgroundType: deriveHeroBackgroundType(template.header?.hero_section?.image_url),
    backgroundImageSize:
      template.header?.hero_section?.image_fit === ImageFitEnum.Cover ? ImageSizeEnum.Fill : ImageSizeEnum.Fit,
    backgroundImageUrl: template.header?.hero_section?.image_url || null,
    buttonColor: hexToRgbColor(template.buttons?.action?.color, DEFAULT_BUTTON_COLOR),
  };
};

/**
 * Transforms builder form values back to API format for saving
 * @param builderValues - The builder form values
 * @param defaultBlueprint - Optional default experience blueprint to fill in missing fields
 * @param existingTemplate - Optional existing template to preserve metadata
 * @returns Partial template object ready for API
 */
export const transformBuilderValuesToClaimLinkTemplate = (
  builderValues: IClaimLinkBuilderValues,
  defaultBlueprint?: Partial<IClaimLinkTemplate>,
  existingTemplate?: Partial<IClaimLinkTemplate>,
): Partial<IClaimLinkTemplate> => {
  // Convert RGB color to hex format
  const formatColor = (rgb: { r: number; g: number; b: number; a?: number } | null): string => {
    if (!rgb) {
      return ''; // Return empty string if color is null/undefined
    }
    const r = Math.round(rgb.r).toString(16).padStart(2, '0');
    const g = Math.round(rgb.g).toString(16).padStart(2, '0');
    const b = Math.round(rgb.b).toString(16).padStart(2, '0');
    return `#${r}${g}${b}`;
  };

  // Helper to build page data with overrides
  const buildPageData = (pageKey: ClaimLinkEditorPageEnum) => {
    const pageData = builderValues.pages[pageKey];
    const overrides = pageData?.overrides || {};

    // Only include overrides that are explicitly set (not undefined)
    const apiOverrides: IClaimLinkTemplatePageOverrides = {};

    const isHeroBackgroundColorActive = overrides.heroSectionBackgroundType === BackgroundTypesEnum.Color;

    if (
      overrides.headerMediaUrl !== undefined ||
      overrides.logoImageUrl !== undefined ||
      overrides.logoImageSize !== undefined ||
      overrides.heroSectionBackgroundColor !== undefined ||
      overrides.heroSectionImageUrl !== undefined ||
      overrides.heroSectionImageFit !== undefined ||
      overrides.appBarBackgroundColor !== undefined ||
      // Color toggled active on this page with no other hero field set still needs a header
      // block written so the "no image" sentinel below masks the template hero image.
      isHeroBackgroundColorActive
    ) {
      apiOverrides.header = {};

      if (overrides.headerMediaUrl !== undefined) {
        apiOverrides.header.media = {
          image_url: overrides.headerMediaUrl === '' ? HEADER_MEDIA_EMPTY : overrides.headerMediaUrl,
        };
      }

      if (
        overrides.logoImageUrl !== undefined ||
        overrides.logoImageSize !== undefined ||
        overrides.appBarBackgroundColor !== undefined
      ) {
        apiOverrides.header.app_bar = {};
        if (overrides.logoImageUrl !== undefined) {
          apiOverrides.header.app_bar.logo_image_url = overrides.logoImageUrl || '';
        }
        if (overrides.logoImageSize !== undefined) {
          apiOverrides.header.app_bar.logo_image_size = logoSizePercentToRatio(overrides.logoImageSize);
        }
        if (overrides.appBarBackgroundColor !== undefined) {
          const color = formatColor(overrides.appBarBackgroundColor);
          if (color) {
            apiOverrides.header.app_bar.background_color = color;
          }
        }
      }

      if (
        overrides.heroSectionBackgroundColor !== undefined ||
        overrides.heroSectionImageUrl !== undefined ||
        overrides.heroSectionImageFit !== undefined ||
        isHeroBackgroundColorActive
      ) {
        apiOverrides.header.hero_section = {};
        if (overrides.heroSectionBackgroundColor !== undefined) {
          const color = formatColor(overrides.heroSectionBackgroundColor);
          if (color) {
            apiOverrides.header.hero_section.background_color = color;
          }
        }
        if (isHeroBackgroundColorActive) {
          // Color is the active selection on this page: persist "no image" so the claim page
          // renders the color. The uploaded image URL is kept only in builder state — the API
          // model has no field to store an inactive image alongside an active-type flag.
          apiOverrides.header.hero_section.image_url = HEADER_MEDIA_EMPTY;
        } else if (overrides.heroSectionImageUrl !== undefined) {
          apiOverrides.header.hero_section.image_url =
            overrides.heroSectionImageUrl === '' ? HEADER_MEDIA_EMPTY : overrides.heroSectionImageUrl;
        }
        if (overrides.heroSectionImageFit !== undefined) {
          apiOverrides.header.hero_section.image_fit = overrides.heroSectionImageFit;
        }
      }
    }

    if (overrides.buttonColor !== undefined) {
      const color = formatColor(overrides.buttonColor);
      if (color) {
        apiOverrides.buttons = {
          action: { color },
        };
      }
    }

    return {
      // Cleared text is saved as visually-empty HTML: a bare '' is dropped/re-defaulted
      // downstream, so the cleared state would not survive a refresh.
      header_text: padEmptyBlocks(divBlocksToParagraphs(pageData.headerText)) || EMPTY_RICH_TEXT_HTML,
      body_text: padEmptyBlocks(divBlocksToParagraphs(pageData.bodyText)) || EMPTY_RICH_TEXT_HTML,
      ...(Object.keys(apiOverrides).length > 0 ? { overrides: apiOverrides } : {}),
    };
  };

  // Ensure department_ids is never empty - use ["no_department"] if no departments selected
  const departmentIds = builderValues.selectedDepartmentIds.filter((id) => id !== NO_DEPARTMENT);
  const finalDepartmentIds = departmentIds.length > 0 ? departmentIds : [NO_DEPARTMENT];

  const logoImageUrlToSend = builderValues.globalLogoImageUrl || '';

  // Persist only the active hero background selection: with Color active the image URL is
  // saved empty (the API model has no active-type field to keep an inactive image around).
  const globalHeroBackgroundType = resolveHeroBackgroundType(
    builderValues.globalHeroSectionBackgroundType,
    builderValues.globalHeroSectionImageUrl,
  );
  const globalHeroImageUrlToSend =
    globalHeroBackgroundType === BackgroundTypesEnum.Image ? builderValues.globalHeroSectionImageUrl || '' : '';

  // Build complete template with global defaults + pages
  return {
    ...existingTemplate,
    name: builderValues.templateName,
    description: existingTemplate?.description || '',
    department_ids: finalDepartmentIds,
    status: builderValues.status,
    cover_image_url: builderValues.coverImageUrl || existingTemplate?.cover_image_url || '',

    // Global defaults at root level
    header: {
      media: {
        image_url: builderValues.globalHeaderMediaUrl || '',
      },
      app_bar: {
        logo_image_url: logoImageUrlToSend,
        logo_image_href: builderValues.globalLogoImageHref || '',
        logo_image_size: logoSizePercentToRatio(builderValues.globalLogoImageSize),
        background_color:
          formatColor(builderValues.globalAppBarBackgroundColor) ||
          existingTemplate?.header?.app_bar?.background_color ||
          defaultBlueprint?.header?.app_bar?.background_color ||
          '#000000',
      },
      hero_section: {
        background_color:
          formatColor(builderValues.globalHeroSectionBackgroundColor) ||
          existingTemplate?.header?.hero_section?.background_color ||
          defaultBlueprint?.header?.hero_section?.background_color ||
          '#000000',
        image_url: globalHeroImageUrlToSend,
        image_position: builderValues.globalHeroSectionImagePosition || CSSBackgroundPositionEnum.Center,
        image_fit: builderValues.globalHeroSectionImageFit || ImageFitEnum.Cover,
      },
    },

    buttons: {
      action: {
        color:
          formatColor(builderValues.globalAccentColor) ||
          existingTemplate?.buttons?.action?.color ||
          defaultBlueprint?.buttons?.action?.color ||
          '#000000',
        radius: builderValues.globalButtonRadius,
      },
      stepper: existingTemplate?.buttons?.stepper ||
        defaultBlueprint?.buttons?.stepper || {
          color: formatColor(builderValues.globalAccentColor),
        },
    },

    background: {
      color: formatColor(builderValues.backgroundColor),
    },

    typography: existingTemplate?.typography || defaultBlueprint?.typography,
    footer_color: existingTemplate?.footer_color || defaultBlueprint?.footer_color,

    // Build pages with overrides
    pages: {
      pick_your_gift: buildPageData(ClaimLinkEditorPageEnum.PickYourGift),
      pick_your_option: buildPageData(ClaimLinkEditorPageEnum.ProductOptions),
      shipping_page: buildPageData(ClaimLinkEditorPageEnum.ShippingDetails),
    },
  };
};

// reducer helpers to build the response from the builder form
export const computeDeprecatedBackgroundFields = (
  heroImageUrl: string | null | undefined,
  heroFit: ImageFitEnum | undefined,
): Pick<IClaimLinkBuilderValues, 'backgroundType' | 'backgroundImageSize'> => {
  return {
    backgroundType: deriveHeroBackgroundType(heroImageUrl),
    backgroundImageSize: heroFit === ImageFitEnum.Cover ? ImageSizeEnum.Fill : ImageSizeEnum.Fit,
  };
};

export const withSyncedDeprecatedFields = <T extends Partial<IClaimLinkBuilderValues>>(input: T): T => {
  const out: T = { ...input };

  if (input.globalLogoImageUrl !== undefined) {
    out.logoImageUrl = input.globalLogoImageUrl;
  }
  if (input.globalHeaderMediaUrl !== undefined) {
    out.headerMediaUrl = input.globalHeaderMediaUrl;
  }
  if (input.globalAccentColor !== undefined) {
    out.buttonColor = input.globalAccentColor;
  }

  if (input.globalHeroSectionImageUrl !== undefined) {
    out.backgroundImageUrl = input.globalHeroSectionImageUrl;
    Object.assign(
      out,
      computeDeprecatedBackgroundFields(input.globalHeroSectionImageUrl, input.globalHeroSectionImageFit),
    );
  }

  if (input.globalHeroSectionImageFit !== undefined) {
    // when only fit changes, derive size from current (already-updated) backgroundImageUrl if present
    Object.assign(out, computeDeprecatedBackgroundFields(out.backgroundImageUrl, input.globalHeroSectionImageFit));
  }

  return out;
};

export const mergePages = (
  current: IClaimLinkBuilderValues['pages'],
  updates: Partial<IClaimLinkBuilderValues>['pages'],
): IClaimLinkBuilderValues['pages'] => {
  if (!updates) {
    return current;
  }

  const merged = { ...current };

  (Object.keys(updates) as (keyof typeof merged)[]).forEach((pageKey) => {
    const nextPage = updates[pageKey];
    if (!nextPage) {
      return;
    }

    merged[pageKey] = {
      ...merged[pageKey],
      ...nextPage,
      overrides: {
        ...merged[pageKey].overrides,
        ...(nextPage.overrides || {}),
      },
    };
  });

  return merged;
};

/**
 * Validates that all required header_text fields are filled
 * This validation is only applied when publishing a template (making it Live)
 * Draft templates can be saved without validation
 * Returns error object matching backend schema if validation fails
 */
// A cleared rich-text header is persisted as EMPTY_RICH_TEXT_HTML (or equivalent empty TinyMCE
// markup), so a plain .trim() check would treat it as non-empty. Strip tags and non-breaking
// spaces to detect a header that renders blank on the claim page.
const isRichTextHeaderEmpty = (headerText?: string): boolean =>
  !headerText
    ?.replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .trim();

export const validateTemplateHeaderTexts = (
  draft: IClaimLinkBuilderValues,
): { errors: IClaimLinkTemplateErrors; message: string } | null => {
  const errors: IClaimLinkTemplateErrors = { pages: {} };
  let hasErrors = false;

  if (isRichTextHeaderEmpty(draft.pages?.[ClaimLinkEditorPageEnum.PickYourGift]?.headerText)) {
    errors.pages!.pick_your_gift = {
      header_text: HEADER_TEXT_REQUIRED_ERROR,
    };
    hasErrors = true;
  }

  if (isRichTextHeaderEmpty(draft.pages?.[ClaimLinkEditorPageEnum.ProductOptions]?.headerText)) {
    errors.pages!.pick_your_option = {
      header_text: HEADER_TEXT_REQUIRED_ERROR,
    };
    hasErrors = true;
  }

  if (isRichTextHeaderEmpty(draft.pages?.[ClaimLinkEditorPageEnum.ShippingDetails]?.headerText)) {
    errors.pages!.shipping_page = {
      header_text: HEADER_TEXT_REQUIRED_ERROR,
    };
    hasErrors = true;
  }

  if (hasErrors) {
    return {
      errors,
      message: 'Please correct the following errors:',
    };
  }

  return null;
};

/**
 * Backend page key to ClaimLinkEditorPageEnum mapping
 */
const BACKEND_PAGE_MAP: Record<string, ClaimLinkEditorPageEnum> = {
  pick_your_gift: ClaimLinkEditorPageEnum.PickYourGift,
  pick_your_option: ClaimLinkEditorPageEnum.ProductOptions,
  shipping_page: ClaimLinkEditorPageEnum.ShippingDetails,
};

/**
 * Get the count of validation errors for a specific page
 *
 * @param pageEnum - The page to count errors for
 * @param validationErrors - Validation errors from Redux state
 * @param shouldShowError - Function to check if error should be shown (respects cleared errors)
 * @returns Number of visible errors on the page
 *
 * @example
 * const errorCount = getPageErrorCount(
 *   ClaimLinkEditorPageEnum.PickYourGift,
 *   validationErrors,
 *   shouldShowError
 * );
 */
export const getPageErrorCount = (
  pageEnum: ClaimLinkEditorPageEnum,
  validationErrors: IClaimLinkTemplateErrors | null,
  shouldShowError: (fieldConstant: string, pageId?: ClaimLinkEditorPageEnum) => boolean,
): number => {
  const pages = validationErrors?.pages;
  if (!pages) {
    return 0;
  }

  let errorCount = 0;

  // Check each backend page key
  (Object.entries(pages) as [keyof typeof pages, (typeof pages)[keyof typeof pages]][]).forEach(([key, pageErrors]) => {
    const enumValue = BACKEND_PAGE_MAP[key];
    if (enumValue === pageEnum && pageErrors) {
      // Check header_text error (only count if not cleared by user)
      if (pageErrors.header_text && shouldShowError('headerText', pageEnum)) {
        errorCount++;
      }
    }
  });

  return errorCount;
};

/**
 * Maps backend page keys to ClaimLinkEditorPageEnum
 */
export const mapBackendPageKeyToEnum = (key: string): ClaimLinkEditorPageEnum | null => {
  return BACKEND_PAGE_MAP[key] || null;
};

/**
 * Find field configuration by field constant
 */
export const getFieldConfig = (fieldConstant: string): IValidatedFieldConfig | undefined => {
  return VALIDATED_FIELDS_CONFIG.find((config) => config.fieldConstant === fieldConstant);
};

/**
 * Get all page-specific field configs
 * Page fields have {pageKey} placeholder in errorPath
 */
export const getPageFieldConfigs = (): IValidatedFieldConfig[] => {
  return VALIDATED_FIELDS_CONFIG.filter((config) => config.errorPath.includes('{pageKey}'));
};

// ============================================================================
// Field Mapping Utilities
// ============================================================================

/**
 * Maps a field constant to its corresponding value in a page object
 * Used for extracting field values for validation or transformation
 *
 * @param fieldConstant - The field constant (HEADER_TEXT, BODY_TEXT, etc.)
 * @param page - The page object containing field values
 * @returns The field value, or undefined if not found
 *
 * @example
 * const headerText = getPageFieldValue('headerText', page);
 * const bodyText = getPageFieldValue('bodyText', page);
 */
export const getPageFieldValue = (fieldConstant: string, page: IClaimLinkBuilderPageValues): TPageFieldValue => {
  // Import constants inline to avoid circular dependencies
  const HEADER_TEXT = 'headerText';
  const BODY_TEXT = 'bodyText';
  const HEADER_MEDIA = 'headerMediaUrl';
  const ACCENT_COLOR = 'buttonColor';
  const APP_BAR_BACKGROUND_COLOR = 'appBarBackgroundColor';
  const HERO_SECTION_BACKGROUND_COLOR = 'heroSectionBackgroundColor';
  const HERO_SECTION_IMAGE_URL = 'heroSectionImageUrl';
  const HERO_SECTION_IMAGE_FIT = 'heroSectionImageFit';
  const LOGO_IMAGE = 'logoImageUrl';
  const LOGO_IMAGE_SIZE = 'logoImageSize';

  switch (fieldConstant) {
    case HEADER_TEXT:
      return page.headerText;
    case BODY_TEXT:
      return page.bodyText;
    case HEADER_MEDIA:
      return page.overrides?.headerMediaUrl;
    case ACCENT_COLOR:
      return page.overrides?.buttonColor;
    case APP_BAR_BACKGROUND_COLOR:
      return page.overrides?.appBarBackgroundColor;
    case HERO_SECTION_BACKGROUND_COLOR:
      return page.overrides?.heroSectionBackgroundColor;
    case HERO_SECTION_IMAGE_URL:
      return page.overrides?.heroSectionImageUrl;
    case HERO_SECTION_IMAGE_FIT:
      return page.overrides?.heroSectionImageFit;
    case LOGO_IMAGE:
      return page.overrides?.logoImageUrl;
    case LOGO_IMAGE_SIZE:
      return page.overrides?.logoImageSize;
    default:
      return undefined;
  }
};

// ============================================================================
// Field Initialization Utilities
// ============================================================================

/**
 * Initializes field values for validation tracking from builder pages
 * Maps all page data to error paths for validation tracking
 *
 * This function extracts values from all pages and calls the initialization
 * callback for each tracked field, enabling real-time error clearing.
 *
 * @param pages - The pages object from IClaimLinkBuilderValues
 * @param pageKeyMap - Mapping from page enum to backend page key
 * @param initializeFieldValue - Callback to initialize each field (typically from useValidationErrorClearing hook)
 *
 * @example
 * useEffect(() => {
 *   initializeClaimLinkBuilderFieldTracking(pages, PAGE_KEY_MAP, initializeFieldValue);
 * }, []);
 */
export const initializeClaimLinkBuilderFieldTracking = (
  pages: IClaimLinkBuilderValues['pages'],
  pageKeyMap: Record<ClaimLinkEditorPageEnum, string>,
  initializeFieldValue: (fieldKey: string, value: TPageFieldValue) => void,
): void => {
  const pageFieldConfigs = getPageFieldConfigs();

  Object.entries(pages).forEach(([pageId, page]) => {
    const pageEnum = pageId as ClaimLinkEditorPageEnum;
    const backendPageKey = pageKeyMap[pageEnum];

    if (backendPageKey) {
      // Initialize all tracked page fields
      pageFieldConfigs.forEach((config) => {
        const fieldKey = config.errorPath.replace('{pageKey}', backendPageKey);
        const fieldValue = getPageFieldValue(config.fieldConstant, page);
        initializeFieldValue(fieldKey, fieldValue);
      });
    }
  });
};

// ============================================================================
// Validation & Error Utilities
// ============================================================================

/**
 * Gets the validation error for a specific field on a page
 * Checks if error should be shown (not cleared by user) and maps page enum to backend key
 *
 * @param fieldConstant - The field constant (e.g., HEADER_TEXT)
 * @param pageEnum - The page enum value
 * @param validationErrors - Validation errors from Redux state
 * @param shouldShowError - Function to check if error should be shown (from useValidationErrorClearing hook)
 * @returns The error message, or undefined if no error or error is cleared
 *
 * @example
 * const headerError = getPageFieldError(
 *   'headerText',
 *   ClaimLinkEditorPageEnum.PickYourGift,
 *   validationErrors,
 *   shouldShowError
 * );
 */
export const getPageFieldError = (
  fieldConstant: string,
  pageEnum: ClaimLinkEditorPageEnum,
  validationErrors: IClaimLinkTemplateErrors | null,
  shouldShowError: (fieldConstant: string, pageId?: ClaimLinkEditorPageEnum) => boolean,
): string | undefined => {
  // Check if error should be shown (not cleared by user editing)
  if (!shouldShowError(fieldConstant, pageEnum)) {
    return undefined;
  }

  if (!validationErrors?.pages) {
    return undefined;
  }

  // Find backend page key that maps to current enum
  const pageKeys = Object.keys(validationErrors.pages);
  for (const key of pageKeys) {
    const enumValue = mapBackendPageKeyToEnum(key);
    if (enumValue === pageEnum) {
      const config = getFieldConfig(fieldConstant);
      if (!config) {
        return undefined;
      }

      // Extract the field name from error path (e.g., 'header_text' from 'pages.{pageKey}.header_text')
      const fieldName = config.errorPath.split('.').pop();
      if (!fieldName) {
        return undefined;
      }

      const pageErrors = validationErrors.pages[key as keyof typeof validationErrors.pages];
      return pageErrors?.[fieldName as keyof typeof pageErrors] as string | undefined;
    }
  }
  return undefined;
};

// ============================================================================
// Override Management Utilities
// ============================================================================

/**
 * Determines if a field has an override value set
 * A field is considered overridden if its value is not undefined
 *
 * @param overrideValue - The override value to check
 * @returns True if field is overridden (value is not undefined)
 *
 * @example
 * const hasOverride = isFieldOverridden(page.overrides?.buttonColor);
 */
export const isFieldOverridden = (overrideValue: any): boolean => {
  return overrideValue !== undefined;
};

/**
 * Gets all related fields that should be cleared when a field override is cleared
 * For example, clearing heroSectionImageUrl should also clear heroSectionImageFit
 *
 * @param field - The field being cleared
 * @returns Array of all fields that should be cleared (including the original field)
 *
 * @example
 * const fieldsToClear = getRelatedOverrideFields('heroSectionImageUrl');
 * // Returns: ['heroSectionImageUrl', 'heroSectionImageFit']
 */
export const getRelatedOverrideFields = (field: string): string[] => {
  const HERO_SECTION_IMAGE_URL = 'heroSectionImageUrl';
  const HERO_SECTION_IMAGE_FIT = 'heroSectionImageFit';
  const HERO_SECTION_BACKGROUND_TYPE = 'heroSectionBackgroundType';

  // Special case: when clearing heroSectionImageUrl, also clear the fit (which carries the
  // hero height mode) and the page's active background type so the page fully inherits the
  // template default.
  if (field === HERO_SECTION_IMAGE_URL) {
    return [HERO_SECTION_IMAGE_URL, HERO_SECTION_IMAGE_FIT, HERO_SECTION_BACKGROUND_TYPE];
  }

  // Default: only clear the specified field
  return [field];
};

// ============================================================================
// Error Parsing Utilities
// ============================================================================

/**
 * Parses and groups template validation errors by page and type
 * Separates page-specific errors from global template errors
 *
 * @param errors - Validation errors from API response
 * @param pageNames - Mapping from backend page key to display name
 * @returns Parsed errors grouped by page with counts
 *
 * @example
 * const parsed = parseTemplateErrors(validationErrors, TEMPLATE_PAGE_NAMES);
 * // Returns: { pageErrors: [...], totalIssues: 5, nonPageErrors: [...] }
 */
export const parseTemplateErrors = (
  errors: IClaimLinkTemplateErrors | null | undefined,
  pageNames: Record<string, string>,
): IParsedTemplateErrors => {
  if (!errors) {
    return { pageErrors: [], totalIssues: 0, nonPageErrors: [] };
  }

  const pages: IPageErrors[] = [];
  let issueCount = 0;
  const otherErrors: string[] = [];

  // Parse page-specific errors
  if (errors.pages) {
    Object.entries(errors.pages).forEach(([pageKey, pageErrorObj]) => {
      if (pageErrorObj) {
        const pageName = pageNames[pageKey] || pageKey;
        const pageIssues: string[] = [];

        if (pageErrorObj.header_text) {
          pageIssues.push(pageErrorObj.header_text);
          issueCount++;
        }

        if (pageIssues.length > 0) {
          pages.push({ pageName, errors: pageIssues });
        }
      }
    });
  }

  // Parse global/non-page errors
  if (errors.department_ids) {
    otherErrors.push(errors.department_ids);
    issueCount++;
  }
  if (errors.name) {
    otherErrors.push(errors.name);
    issueCount++;
  }
  if (errors.status) {
    otherErrors.push(errors.status);
    issueCount++;
  }
  if (errors.header?.app_bar?.background_color) {
    otherErrors.push(`App bar background color: ${errors.header.app_bar.background_color}`);
    issueCount++;
  }
  if (errors.header?.hero_section?.background_color) {
    otherErrors.push(`Hero section background color: ${errors.header.hero_section.background_color}`);
    issueCount++;
  }
  if (errors.buttons?.action?.color) {
    otherErrors.push(`Action button color: ${errors.buttons.action.color}`);
    issueCount++;
  }
  if (errors.buttons?.action?.radius) {
    otherErrors.push(`Action button radius: ${errors.buttons.action.radius}`);
    issueCount++;
  }
  if (errors.buttons?.stepper?.color) {
    otherErrors.push(`Stepper button color: ${errors.buttons.stepper.color}`);
    issueCount++;
  }

  return {
    pageErrors: pages,
    totalIssues: issueCount,
    nonPageErrors: otherErrors,
  };
};

export const getIframeStepByPage = (page: ClaimLinkEditorPageEnum): IframeStepEnum => {
  switch (page) {
    case ClaimLinkEditorPageEnum.PickYourGift:
      return IframeStepEnum.PickYourGift;
    case ClaimLinkEditorPageEnum.ProductOptions:
      return IframeStepEnum.CustomizeOptions;
    case ClaimLinkEditorPageEnum.ShippingDetails:
      return IframeStepEnum.Address;
    default:
      return IframeStepEnum.PickYourGift;
  }
};

export const hasDepartmentRestrictions = (departmentIds?: string[]): boolean => {
  return (departmentIds?.length ?? 0) > 0 && !departmentIds?.includes(NO_DEPARTMENT);
};

export const getDepartmentConfirmMessage = (departmentIds: string[], departmentList: IDepartment[]): string => {
  const departmentNames = departmentIds
    .map((id) => departmentList.find((dept) => dept.uid === id)?.name)
    .filter(Boolean);

  const deptText = departmentNames.length > 0 ? departmentNames.join(', ') : '';

  return `This experience is currently available to the ${deptText} department${
    departmentNames.length > 1 ? 's' : ''
  }. Making this template the organization default will remove the department setting and make this template available across the whole organization.`;
};
